import type { DistributiveOmit } from '@tanstack/react-query'
import { match } from 'ts-pattern'
import type { AreEquivalent } from '../types.ts'
import {
  CreateRepositoryIntegrationRequestSchema,
  RepositoryTypeSchema,
  type CreateRepositoryIntegrationRequest,
  type Integration,
  type Repository,
  type RepositoryIntegration,
  type RepositoryType,
  type ScanResponse,
  type Tool,
} from './api-client/api-schemas'

export type ToolIntegration = Extract<
  Integration,
  {
    type:
      | 'sonar'
      | 'checkmarx'
      | 'appscan'
      | 'veracode'
      | 'arnica'
      | 'datadog'
      | 'polaris'
      | 'black-duck-hub'
      | 'checkmarx-enterprise'
  }
>

export const TOOL_INTEGRATION_TYPES: readonly ToolIntegration['type'][] =
  CreateRepositoryIntegrationRequestSchema.options.map(option => option.shape.type.value)

true satisfies AreEquivalent<ToolIntegration['type'], CreateRepositoryIntegrationRequest['type']>

/**
 * The integration types that are source providers rather than finding producers. Derived from
 * `RepositoryTypeSchema` minus the bare `git` case, which is a repository kind with no integration.
 */
export type ScmIntegrationType = Exclude<RepositoryType, 'git'>

export const SCM_INTEGRATION_TYPES = RepositoryTypeSchema.options.filter(
  (type): type is ScmIntegrationType => type !== 'git'
)

const SCM_INTEGRATION_TYPE_SET = new Set<string>(SCM_INTEGRATION_TYPES)

export const getIsScmIntegrationType = (type: Integration['type']): type is ScmIntegrationType =>
  SCM_INTEGRATION_TYPE_SET.has(type)

const TOOL_INTEGRATION_TYPE_SET = new Set<string>(TOOL_INTEGRATION_TYPES)

export const isToolIntegration = (integration: Integration): integration is ToolIntegration =>
  TOOL_INTEGRATION_TYPE_SET.has(integration.type)

/**
 * The type-level counterpart to `isToolIntegration`, mirroring `getIsScmIntegrationType`. Needed
 * where only the type is in hand rather than a whole integration — narrowing the tool types out of
 * `GET /api/v1/integration-types`, which reports every type including the source providers.
 */
export const getIsToolIntegrationType = (type: Integration['type']): type is ToolIntegration['type'] =>
  TOOL_INTEGRATION_TYPE_SET.has(type)

/**
 * The integration type that owns a tool's findings, or undefined for a tool with no integration of
 * its own (an uploaded SARIF, or a tool Pixee reads through another provider).
 */
export function convertToolToIntegrationType(tool: Tool): ToolIntegration['type'] | undefined {
  return match(tool)
    .with('arnica_sast', () => 'arnica' as const)
    .with('datadog_sast', () => 'datadog' as const)
    .with('polaris_sast', 'polaris_sca', () => 'polaris' as const)
    .with('black_duck_hub_sca', () => 'black-duck-hub' as const)
    .with('checkmarx_enterprise', () => 'checkmarx-enterprise' as const)
    .with('sonar', 'checkmarx', 'appscan', 'veracode', t => t)
    .otherwise(() => undefined)
}

export function convertIntegrationTypeToTool(type: RepositoryIntegration['type']): Tool {
  return (
    match(type)
      .with('arnica', () => 'arnica_sast' as const)
      .with('datadog', () => 'datadog_sast' as const)
      // One Polaris mapping covers both Polaris tools; SAST stands in for it wherever a single tool
      // is needed, such as the icon on its settings row.
      .with('polaris', () => 'polaris_sast' as const)
      .with('black-duck-hub', () => 'black_duck_hub_sca' as const)
      .with('checkmarx-enterprise', () => 'checkmarx_enterprise' as const)
      .otherwise(t => t)
  )
}

/**
 * Names an integration *type*. Split out of `getIntegrationName` so a surface holding only the type
 * — a tool filter, a picker's type control — reads the same table as one holding the integration,
 * instead of routing through the long-form tool names and calling Polaris something else.
 */
export function getIntegrationTypeName(type: Integration['type']): string {
  return match(type)
    .with('polaris', () => 'Polaris')
    .with('sonar', () => 'SonarQube')
    .with('checkmarx', () => 'Checkmarx')
    .with('veracode', () => 'Veracode')
    .with('appscan', () => 'AppScan')
    .with('arnica', () => 'Arnica')
    .with('datadog', () => 'Datadog')
    .with('black-duck-hub', () => 'Black Duck Hub')
    .with('checkmarx-enterprise', () => 'Checkmarx Enterprise')
    .otherwise(other => (other.length > 0 ? other.charAt(0).toUpperCase() + other.slice(1) : 'external tool'))
}

export function getIntegrationName(integration: Integration | null | undefined): string | undefined {
  return integration ? getIntegrationTypeName(integration.type) : undefined
}

/**
 * The producing integration instance's name. `_embedded.integration` is omitted for disconnected
 * scans, so callers render nothing rather than an empty label.
 */
export function getScanInstanceName(scan: Pick<ScanResponse, '_embedded'>): string | undefined {
  return scan._embedded?.integration?.name
}

/**
 * What a tool calls the identifiers tying one of its projects to a repository, or an empty list for
 * a tool with no repository mapping. Shared because the mapping form asks for them and the removal
 * warning names what is discarded.
 */
export function getRepositoryIdentifierLabels(tool: Tool | undefined): readonly string[] {
  if (tool === undefined) {
    return []
  }
  return (
    match<Tool, readonly string[]>(tool)
      // The stored key, which the mapped row shows and the removal warning discards. The mapping form
      // is the one exception to sharing this label: it picks the key by project name, as "Sonar
      // project" (`SonarProjectSelector`), since a field showing a name must not claim to be a key.
      .with('sonar', () => ['Project key'])
      .with('checkmarx', () => ['Project ID'])
      .with('appscan', () => ['Application ID'])
      .with('veracode', () => ['Application GUID'])
      .with('arnica_sast', () => ['Asset key'])
      .with('datadog_sast', () => ['Repository ID'])
      // A Hub mapping names a project *version*, so both UUIDs are stored and both are discarded with
      // it. The removal warning reads this list, which is why it is the whole list and not the first.
      .with('black_duck_hub_sca', () => ['Project ID', 'Version ID'])
      .with('checkmarx_enterprise', () => ['Project ID'])
      .with('polaris_sast', 'polaris_sca', () => ['Project ID'])
      .otherwise(() => [])
  )
}

/**
 * The first identifier, for the mapping form's own field. Derived from
 * {@link getRepositoryIdentifierLabels} rather than repeating it, so a tool cannot end up labelled
 * one way in the form and another in the removal warning. Sonar alone is not typed into this field;
 * see its entry in that table.
 */
export function getRepositoryIdentifierLabel(tool: Tool | undefined): string | undefined {
  return getRepositoryIdentifierLabels(tool)[0]
}

// Only the first character is lowered: every label but "Project key" leads with an acronym, which
// `toLowerCase()` would mangle.
export function getRepositoryIdentifierPlaceholder(tool: Tool | undefined): string | undefined {
  const label = getRepositoryIdentifierLabel(tool)
  return label === undefined ? undefined : `Enter ${label.charAt(0).toLowerCase()}${label.slice(1)}`
}

export function getToolName(tool: undefined): undefined
export function getToolName(tool: Tool): string
export function getToolName(tool: Tool | undefined): string | undefined
export function getToolName(tool: Tool | undefined) {
  if (!tool) {
    return undefined
  }

  return match(tool)
    .with('aikido_sast', () => 'Aikido SAST')
    .with('aikido_sca', () => 'Aikido SCA')
    .with('appscan', () => 'AppScan')
    .with('polaris_sast', () => 'Black Duck Polaris SAST')
    .with('polaris_sca', () => 'Black Duck Polaris SCA')
    .with('black_duck_hub_sca', () => 'Black Duck Hub SCA')
    .with('checkmarx', () => 'Checkmarx')
    .with('checkmarx_enterprise', () => 'Checkmarx Enterprise')
    .with('codeql', () => 'CodeQL')
    .with('contrast', () => 'Contrast')
    .with('fortify', () => 'Fortify')
    .with('gitlab_sast', () => 'GitLab SAST')
    .with('gitlab_dependency_scanning', () => 'GitLab SCA')
    .with('dependabot', () => 'Dependabot')
    .with('semgrep', () => 'Semgrep')
    .with('snyk', () => 'Snyk')
    .with('sonar', () => 'SonarQube')
    .with('veracode', () => 'Veracode')
    .with('datadog_sast', () => 'Datadog')
    .with('arnica_sast', () => 'Arnica')
    .with('gitleaks', () => 'Gitleaks')
    .with('pixee_ai', () => 'Pixee Beacon')
    .with('freeform', () => 'Findings intake')
    .with('zap', () => 'OWASP ZAP')
    .exhaustive()
}

/** Orders tools alphabetically by display name, which is how every tool list should read. */
export const sortToolsByName = (tools: Iterable<Tool>): Tool[] =>
  [...tools].sort((a, b) => getToolName(a).localeCompare(getToolName(b)))

/**
 * Returns the repository name from a GitHub repository name in format "org/repo"
 * @param gitHubRepositoryNameIncludingOrg - The GitHub repository name in format "org/repo"
 * @returns The repository name
 */
function getGitHubRepositoryName(gitHubRepositoryNameIncludingOrg: string): string {
  const [_, repo] = gitHubRepositoryNameIncludingOrg.split('/')

  if (!repo) {
    console.warn(`Failed to parse repository name from ${gitHubRepositoryNameIncludingOrg}`)
    return gitHubRepositoryNameIncludingOrg
  }

  return repo
}

/**
 * Returns the organization name from a GitHub repository name in format "org/repo"
 * @param gitHubRepositoryNameIncludingOrg - The GitHub repository name in format "org/repo"
 * @returns The organization name
 */
function getGitHubOrganizationName(gitHubRepositoryNameIncludingOrg: string): string {
  const [org] = gitHubRepositoryNameIncludingOrg.split('/')

  if (!org) {
    console.warn(`Failed to parse organization name from ${gitHubRepositoryNameIncludingOrg}`)
    return gitHubRepositoryNameIncludingOrg
  }

  return org
}

export const getRepositoryTypeDisplayName = (type: Repository['type']) =>
  match(type)
    .with('azure', () => 'Azure')
    .with('github', () => 'GitHub')
    .with('gitlab', () => 'GitLab')
    .with('bitbucket', () => 'Bitbucket')
    .with('git', () => 'Git')
    .exhaustive()

export const getRepositoryOrganization = (repository: DistributiveOmit<Repository, '_links'>) =>
  match(repository)
    .with({ type: 'azure' }, repo => repo.organization)
    .with({ type: 'gitlab' }, repo => repo.namespace)
    .with({ type: 'github' }, repo => getGitHubOrganizationName(repo.full_name))
    .with({ type: 'bitbucket' }, repo => repo.workspace)
    .with({ type: 'git' }, () => 'no-organization')
    .exhaustive()

export const getRepositoryName = (repository: DistributiveOmit<Repository, '_links'>) =>
  match(repository)
    .with({ type: 'azure' }, repo => repo.project)
    .with({ type: 'github' }, repo => getGitHubRepositoryName(repo.full_name))
    .with({ type: 'gitlab' }, repo => repo.name)
    .with({ type: 'bitbucket' }, repo => repo.name)
    .with({ type: 'git' }, repo => repo.name)
    .exhaustive()
